import { Component, OnDestroy, OnInit } from "@angular/core";


import { RouterModule } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { NextPage, NextPageService, NavigationLocation, PageLayout, SearchType, SliderType } from "@cvx/nextpage";
import { ChatContainerComponent } from "./chat/chat-container/chat-container.component";
import { SpinnerComponent } from "./shared/spinner/spinner.component";
import { AppShellSkeletonComponent } from "./shared/app-shell-skeleton/app-shell-skeleton.component";
import { UserRequestsDataService } from "./services/administration/user-requests.service";
import { IUserData } from "./interfaces/administration/userData";
import { ILoggedUserData } from "./interfaces/common/loggedUserData";
import { Observable, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { LoggedUserService } from "./services/common/logged-user.service";
import { RoutesService } from "./services/routes.service";
import { CalAngularService } from "@cvx/cal-angular";
import { HealthService } from "./services/common/health.service";
import { UserContextService } from "./services/common/user-context.service";
import { Role } from "./models/authorization";
import { NavigationEnd, Router } from '@angular/router';
import { menuRoutes } from "./app-routing.module";

@Component({
  selector: "app-root",
  imports: [
    RouterModule,
    MatIconModule,
    MatButtonModule,
    NextPage,
    ChatContainerComponent,
    SpinnerComponent,
    AppShellSkeletonComponent
],
  templateUrl: "app.component.html",
  styleUrls: ["./app.component.scss"]
})
export class AppComponent implements OnInit, OnDestroy {
  NavigationLocation = NavigationLocation;
  PageLayout = PageLayout;
  SearchType = SearchType;
  SliderType = SliderType;
  loggedUsername: string = "";
  userRoles: Role[] = [];
  filteredRoutesLoaded: boolean = false;
  dataLoading: boolean = true;
  appVersion: string = "";
  isLogoutPage: boolean = false;
  currentAssessmentId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  private readonly nextPageNavGuardFlag = '__amoreNavContainerGuardInstalled';

  get isOnAssessmentPage(): boolean {
    return this.currentAssessmentId !== null;
  }

  constructor(
    private NP: NextPageService,
    private loggedUserService: LoggedUserService,
    private authService: CalAngularService,
    private userRequestsDataService: UserRequestsDataService,
    private routesService: RoutesService,
    private healthService: HealthService,
    private userContextService: UserContextService, 
    private router: Router
  ) {
    this.installNextPageNavContainerGuard();
    this.NP.options.sitename = "AI Modernization Evaluator";
    this.setLoggedUserInfo();
    this.loadAppVersion();
  }

  private installNextPageNavContainerGuard(): void {
    const layout = this.NP.UI.layout as typeof this.NP.UI.layout & {
      [key: string]: unknown;
      checkNavContainerHeight?: (properties: unknown) => void;
    };

    if (layout[this.nextPageNavGuardFlag]) {
      return;
    }

    const originalCheckNavContainerHeight = layout.checkNavContainerHeight?.bind(layout);
    if (!originalCheckNavContainerHeight) {
      return;
    }

    layout[this.nextPageNavGuardFlag] = true;
    layout.checkNavContainerHeight = (properties: unknown) => {
      const navContainer = document.getElementById('nav-container');
      if (!navContainer) {
        return;
      }

      const activeSubNav = navContainer.querySelector('.subnav.active');
      const mainNav = navContainer.querySelector('.mainnav');
      if (!activeSubNav && !mainNav) {
        return;
      }

      originalCheckNavContainerHeight(properties);
    };
  }

  ngOnInit() {
    this.dataLoading = true;

    // Track route changes to detect logout page and extract assessment ID.
    // Filter to NavigationEnd only — router.events fires multiple times per navigation.
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isLogoutPage = this.router.url === '/logout';
        this.extractAssessmentIdFromUrl();
      });
    this.isLogoutPage = this.router.url === '/logout';
    this.extractAssessmentIdFromUrl();

    // Subscribe to filteredRoutesLoaded FIRST (before triggering it)
    this.routesService.filteredRoutesLoaded.subscribe((loaded) => {
      if (loaded) {
        this.filteredRoutesLoaded = loaded;
        this.dataLoading = false;
      }
    });
    
    // Initialize user context (roles and permissions)
    this.userContextService.initialize().subscribe({
      next: (context) => {
        // Store user roles for display
        this.userRoles = context.roles;
        
        // Trigger route filtering after context is loaded
        this.routesService.filterRoutesForMenu(menuRoutes);
      },
      error: (error) => {
        console.error('Failed to initialize user context', error);
        this.dataLoading = false;
      }
    });
  }

  setLoggedUserInfo() {
    // isUserSignedIn() returns an Observable, so it must be subscribed to rather than tested for truthiness.
    this.authService.isUserSignedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isSignedIn) => {
        if (!isSignedIn) {
          this.authService.userInitiatedSignIn();
          return;
        }

        this.loggedUsername = this.authService.getAccount()?.username ?? "";
        this.getLoggedUserInfo().subscribe((loggedUserData) => {
          if (loggedUserData !== null) {
            this.loggedUserService.setLoggedUserData(loggedUserData);
          }
        });
      });
  }

  getLoggedUserInfo(): Observable<ILoggedUserData> {
    return this.userRequestsDataService
      .getUserByEmail(this.loggedUsername)
      .pipe(
        map((data) => {
          const userData: IUserData = data;
          this.userRequestsDataService.setActualLoggedUserData(userData);
          const loggedUserData: ILoggedUserData = {
            userId: userData.userId,
            cai: userData.cai,
            email: userData.email,
            isSystemAdministrator: userData.isSystemAdministrator,
            isQualityAssuranceAndControlUser:
              userData.isQualityAssuranceAndControlUser,
            isProjectTeamMember: userData.isProjectTeamMember,
            isQualityAssuranceAdministrator:
              userData.isQualityAssuranceAdministrator,
          };
          return loggedUserData;
        })
      );
  }

  loadAppVersion() {
    this.healthService.getHealthStatus().subscribe({
      next: (health) => {
        this.appVersion = health.version;
      },
      error: (err) => {
        console.error('Failed to load app version:', err);
        this.appVersion = 'N/A';
      }
    });
  }

  extractAssessmentIdFromUrl(): void {
    const match = this.router.url.match(/^\/assessment\/(\d+)/);
    this.currentAssessmentId = match?.[1] ? parseInt(match[1], 10) : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.loggedUserService.logout();
  }
}
